import { Box, FormControl, InputLabel, MenuItem, OutlinedInput } from "@mui/material";
import { CaseInfo, Cell } from "generated/graphql";
import DebouncedText from "components/DebouncedText";
import PSelect from "components/PSelect";
import { Dispatch, FC, ReactNode } from "react";
import { BooleanCell } from "./BooleanCell";
import { DateCell } from "./DateCell";
import { NumericCell } from "./NumericCell";
import { P } from "theme/typography";
import DescriptionTooltip from "components/FormField/DescriptionTooltip";
import { getCellValidationErrors } from "helpers/FormHelpers";
import { CellValueUnion } from "Backend";

export type CellDensity = "normal" | "dense";

function createValidatedHandler<T>(
  cell: Cell,
  setError: Dispatch<string | undefined> | undefined,
  handler: Dispatch<T>
) {
  return (v: T) => {
    const validationErrors = getCellValidationErrors({ value: v, type: cell.type }, cell.validationConfig || {});
    if (setError && validationErrors.length > 0) {
      setError(validationErrors[0]);
    } else if (setError) {
      setError(undefined);
    }
    handler(v);
  };
}

interface FormFieldCellEditorProps {
  caseInfo?: CaseInfo;
  cell: Cell;
  value: CellValueUnion | undefined;
  setValue: Dispatch<CellValueUnion | undefined>;
  error?: string;
  setError?: (error: string | undefined) => void;
  disabled?: boolean;
  tabular?: boolean;
  label: string;
  variant?: CellDensity;
  isValid?: boolean;
  // For select fields, control whether we display an empty option. Supercedes `cell.optional`.
  displayEmptyOption?: boolean;
}

export const FormFieldCellEditor: FC<FormFieldCellEditorProps> = ({
  caseInfo,
  cell,
  value,
  setValue,
  error,
  setError,
  disabled,
  label,
  isValid,
  variant,
  displayEmptyOption,
}) => {
  let component: ReactNode;
  const inputVariant = "outlined";
  switch (cell.type) {
    case "boolean": {
      component = (
        <BooleanCell label={label} disabled={disabled} value={value as boolean | undefined} setValue={setValue} />
      );
      break;
    }
    case "date": {
      component = (
        <DateCell
          value={value as Date | undefined}
          setValue={setValue}
          setError={setError}
          variant={variant}
          caseInfo={caseInfo}
          disabled={disabled}
          required={!cell.optional}
          label={label}
          inputVariant={variant !== "dense" ? inputVariant : "standard"}
        />
      );
      break;
    }
    case "text": {
      component = (
        <DebouncedText
          textFieldProps={{
            required: !cell.optional,
            disabled: disabled,
            variant: variant !== "dense" ? inputVariant : "standard",
            label,
            error: isValid === false || !!error,
            helperText: error,
          }}
          value={(value as string) || ""}
          onChange={createValidatedHandler(cell, setError, (v) => {
            if (v === "") setValue(undefined);
            else setValue(v);
          })}
        />
      );
      break;
    }
    case "multiSelect": {
      if (value !== undefined && !Array.isArray(value)) {
        component = <P>Error</P>;
        break;
      }
      const multiSelectValue = (value as string[]) || undefined;
      component = (
        <FormControl variant={variant === "dense" ? "standard" : "outlined"} required={!cell.optional}>
          {label && (
            <InputLabel shrink={true} id={`${cell.id}-pselect`}>
              {label}
            </InputLabel>
          )}
          <PSelect
            displayEmpty
            multiple
            labelId={`${cell.id}-pselect`}
            variant={variant !== "dense" ? inputVariant : "standard"}
            placeholder={variant !== "dense" ? cell.name : ""}
            disabled={disabled}
            value={multiSelectValue || []}
            label={label}
            error={isValid === false}
            input={variant !== "dense" ? <OutlinedInput notched label={label} /> : undefined}
            onChange={(evt) => {
              const v = evt.target.value as string[];
              if (v.length === 0) setValue(undefined);
              else setValue(v);
            }}>
            {(cell.selectOptions || []).map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </PSelect>
        </FormControl>
      );
      break;
    }
    case "select": {
      const selectValue = (value as string) || "";
      const shouldDisplayEmptyOption = displayEmptyOption !== undefined ? displayEmptyOption : cell.optional;
      component = (
        <FormControl variant={variant === "dense" ? "standard" : "outlined"} required={!cell.optional}>
          {label && (
            <InputLabel shrink={true} id={`${cell.id}-pselect`}>
              {label}
            </InputLabel>
          )}
          <PSelect
            displayEmpty
            labelId={`${cell.id}-pselect`}
            variant={variant !== "dense" ? inputVariant : "standard"}
            placeholder={variant !== "dense" ? cell.name : ""}
            disabled={disabled}
            value={selectValue}
            label={label}
            error={isValid === false}
            input={variant !== "dense" ? <OutlinedInput notched label={label} /> : undefined}
            onChange={(evt) => {
              const v = evt.target.value as string;
              if (v === "") setValue(undefined);
              else setValue(v);
            }}>
            {shouldDisplayEmptyOption && <MenuItem value="" sx={{ py: 2 }}></MenuItem>}
            {(cell.selectOptions || []).map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </PSelect>
        </FormControl>
      );
      break;
    }
    case "float": {
      component = (
        <NumericCell
          inputVariant={variant !== "dense" ? inputVariant : "standard"}
          integerOnly={false}
          optional={cell.optional}
          disabled={disabled}
          value={value as number | undefined}
          setValue={createValidatedHandler(cell, setError, setValue)}
          label={label}
          variant={variant}
          error={isValid === false || !!error}
          helperText={error}
        />
      );
      break;
    }
    case "integer": {
      component = (
        <NumericCell
          variant={variant}
          inputVariant={variant !== "dense" ? inputVariant : "standard"}
          integerOnly={true}
          optional={cell.optional}
          disabled={disabled}
          value={value as number | undefined}
          setValue={createValidatedHandler(cell, setError, setValue)}
          label={label}
          error={isValid === false || !!error}
          helperText={error}
        />
      );
      break;
    }
    default: {
      component = <></>;
      break;
    }
  }
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        gap: 1,
      }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1,
          "& > *:first-child": {
            width: "100%",
          },
          width: "100%",
        }}>
        {component}
        <DescriptionTooltip description={cell.description} />
      </Box>
    </Box>
  );
};
